import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../config';
import {
  parseServerDate,
  formatDate,
  formatDateTime,
  toDateInputValue,
  toDateTimeInputValue,
  toTimeInputValue
} from '../utils/datetime';

export default function EmployeeReports({ user }) {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const endpoint = user.role === 'admin' ? '/api/reports/admin' : '/api/reports/user';
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setReports(await res.json());
    } catch (e) {}
  };

  const dedupeFields = (fields) => {
    const seen = new Set();
    return fields.filter((f) => {
      if (seen.has(f.id)) return false;
      seen.add(f.id);
      return true;
    });
  };

  const loadReport = async (reportId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/reports/${reportId}/data`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();

        if (data.report.sourceType === 'lookup') {
          const dsRes = await fetch(`${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(data.report.lookupDatasetId)}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (dsRes.ok) {
            const dataset = await dsRes.json();
            const keys = [...new Set([dataset.valueField, ...(dataset.searchFields || []), ...((dataset.metadataSchema || []).map(m => m.key))].filter(Boolean))];
            const baseFields = keys.map(key => {
              const meta = (dataset.metadataSchema || []).find(m => m.key === key);
              return { id: key, label: meta?.label || key, type: meta?.format === 'date' ? 'date' : 'text' };
            });
            const extras = [
              { id: 'createdAt', label: 'Date Created', type: 'date' },
              { id: 'updatedAt', label: 'Date Updated', type: 'date' }
            ];
            setFormFields(dedupeFields([...baseFields, ...extras]));
          }
        } else {
          const formRes = await fetch(`${API_BASE_URL}/api/forms`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (formRes.ok) {
            const forms = await formRes.json();
            const reportFormId = String(data.report.formId);
            const form = forms.find(f => String(f.id) === reportFormId);
            if (form) {
              const parsed = typeof form.fields === 'string' ? JSON.parse(form.fields) : form.fields;
              const baseFields = Array.isArray(parsed) ? parsed.filter(f => f && f.type !== 'section') : [];
              const extras = [
                { id: 'createdAt', label: 'Date Created', type: 'date' },
                { id: 'updatedAt', label: 'Date Complete/Approval', type: 'date' },
                { id: 'initiatorName', label: 'Initiator Name', type: 'text' }
              ];
              setFormFields(dedupeFields([...baseFields, ...extras]));
            }
          }
        }

        setSelectedReport(data.report);
        setReportData(data.data);
        setFilters({});
        setSearchQuery('');
      }
    } catch (e) {}
  };

  const getCellValue = (row, parsedData, colId) => {
    if (colId === 'createdAt' || colId === 'updatedAt') return row[colId];
    if (colId === 'initiatorName') return row.initiatorName || row.initiatorId;
    return parsedData[colId] || '';
  };

  const handleFilterChange = (fieldId, value) => {
    setFilters(prev => ({ ...prev, [fieldId]: value }));
  };

  const getFieldType = (fieldId) => {
    const field = formFields.find(f => f.id === fieldId);
    return field ? field.type : 'text';
  };

  const parseDateStart = (val) => {
    const d = parseServerDate(val);
    if (!d) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const parseDateEnd = (val) => {
    const d = parseServerDate(val);
    if (!d) return null;
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const formatInputDate = (d) => toDateInputValue(d);

  const applyApprovalPreset = (preset) => {
    const now = new Date();
    let start = null;
    let end = null;
    if (preset === 'week') {
      const day = now.getDay();
      start = new Date(now);
      start.setDate(now.getDate() - ((day + 6) % 7));
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    }
    if (preset === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    }
    setFilters(prev => ({
      ...prev,
      updatedAtFrom: start ? formatInputDate(start) : prev.updatedAtFrom,
      updatedAtTo: end ? formatInputDate(end) : prev.updatedAtTo
    }));
  };

  const normalizeValue = (val) => {
    if (val === null || val === undefined) return '';
    if (Array.isArray(val)) return val.map(normalizeValue).join(', ');
    if (typeof val === 'object') {
      if (val.name) return String(val.name);
      if (val.label) return String(val.label);
      if (val.title) return String(val.title);
      if (val.code) return String(val.code);
      const first = Object.values(val).find(v => v !== null && v !== undefined && v !== '');
      return first !== undefined ? normalizeValue(first) : '';
    }
    return String(val);
  };

  const formatDisplayValue = (val, fieldType, fieldId) => {
    if (val === null || val === undefined || val === '') return '';
    if (fieldId === 'createdAt' || fieldId === 'updatedAt' || fieldType === 'datetime') {
      return formatDateTime(val) || normalizeValue(val);
    }
    if (fieldType === 'date') {
      return formatDate(val) || normalizeValue(val);
    }
    if (fieldType === 'time') {
      const normalized = toTimeInputValue(val);
      return normalized || normalizeValue(val);
    }
    return normalizeValue(val);
  };

  const getFilterInputType = (fieldId) => {
    if (fieldId === 'createdAt') return 'date';
    const fieldType = getFieldType(fieldId);
    if (fieldType === 'date') return 'date';
    if (fieldType === 'datetime') return 'datetime-local';
    if (fieldType === 'time') return 'time';
    return 'text';
  };

  const toFilterComparableValue = (fieldId, rawValue) => {
    const fieldType = getFieldType(fieldId);
    if (fieldId === 'createdAt' || fieldId === 'updatedAt' || fieldType === 'date') {
      return toDateInputValue(rawValue).toLowerCase();
    }
    if (fieldType === 'datetime') {
      return toDateTimeInputValue(rawValue).toLowerCase();
    }
    if (fieldType === 'time') {
      return toTimeInputValue(rawValue).toLowerCase();
    }
    return normalizeValue(rawValue).toLowerCase();
  };

  const getFilteredData = () => {
    if (!selectedReport) return [];
    let data = reportData;
    const approvalStart = filters.updatedAtFrom ? parseDateStart(filters.updatedAtFrom) : null;
    const approvalEnd = filters.updatedAtTo ? parseDateEnd(filters.updatedAtTo) : null;

    if (approvalStart || approvalEnd) {
      data = data.filter(item => {
        const target = item.updatedAt ? parseServerDate(item.updatedAt) : null;
        if (!target) return false;
        if (approvalStart && target < approvalStart) return false;
        if (approvalEnd && target > approvalEnd) return false;
        return true;
      });
    }
    
    Object.keys(filters).forEach(key => {
      if (key === 'updatedAtFrom' || key === 'updatedAtTo') return;
      const val = filters[key];
      if (!val) return;
      
      data = data.filter(item => {
        let itemVal = '';
        if (key === 'createdAt' || key === 'updatedAt') {
          itemVal = item[key];
        } else if (key === 'initiatorName') {
          itemVal = item.initiatorName || String(item.initiatorId) || '';
        } else {
          try {
            const fd = typeof item.formData === 'string' ? JSON.parse(item.formData) : item.formData;
            itemVal = fd[key] || '';
          } catch(e) {}
        }

        const filterType = getFilterInputType(key);
        const normalized = toFilterComparableValue(key, itemVal);
        const filterVal = String(val).toLowerCase();
        if (filterType === 'date' || filterType === 'datetime-local' || filterType === 'time') {
          if (filterType === 'datetime-local') {
            return normalized.startsWith(filterVal);
          }
          return normalized === filterVal;
        }
        return normalized.includes(filterVal);
      });
    });

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      data = data.filter(item => {
        let parsedData = {};
        try {
          parsedData = typeof item.formData === 'string' ? JSON.parse(item.formData) : item.formData;
        } catch (e) {}
        return formFields.some(field => {
          const val = getCellValue(item, parsedData, field.id);
          return formatDisplayValue(val, field.type, field.id).toLowerCase().includes(q);
        });
      });
    }

    return data;
  };

  if (selectedReport) {
    let columns = [];
    let availableFilters = [];
    try {
      columns = typeof selectedReport.columnsConfig === 'string' ? JSON.parse(selectedReport.columnsConfig) : selectedReport.columnsConfig;
      availableFilters = typeof selectedReport.filtersConfig === 'string' ? JSON.parse(selectedReport.filtersConfig) : selectedReport.filtersConfig;
    } catch (e) {}

    const filteredData = getFilteredData();

    const exportToExcel = () => {
      const columnLabels = columns.map(colId => {
        const field = formFields.find(f => f.id === colId);
        return field ? field.label : colId;
      });
      const rows = filteredData.map(row => {
        let parsedData = {};
        try {
          parsedData = typeof row.formData === 'string' ? JSON.parse(row.formData) : row.formData;
        } catch (e) {}
        const record = {};
        columns.forEach((colId, i) => {
          const field = formFields.find(f => f.id === colId);
          const val = getCellValue(row, parsedData, colId);
          record[columnLabels[i]] = formatDisplayValue(val, field ? field.type : 'text', colId);
        });
        return record;
      });
      const sheet = XLSX.utils.json_to_sheet(rows);
      sheet['!cols'] = columnLabels.map(label => {
        const maxLen = rows.reduce((max, r) => Math.max(max, String(r[label] ?? '').length), label.length);
        return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Report');
      XLSX.writeFile(workbook, `${selectedReport.name || 'report'}.xlsx`);
    };

    return (
      <div className="reports-shell">
        <div className="reports-topbar">
          <button className="button-outline" onClick={() => setSelectedReport(null)}>
            Back
          </button>
          <div className="reports-heading">{selectedReport.name}</div>
          <button className="button-primary" onClick={exportToExcel} disabled={filteredData.length === 0}>
            Export to Excel
          </button>
        </div>

        <div className="filters-panel">
          <div className="filter-item">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search anything..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {availableFilters.map(filterId => {
            const field = formFields.find(f => f.id === filterId);
            if (!field) return null;
            if (filterId === 'updatedAt') {
              return (
                <div key={`filter-${filterId}`} className="filter-item filter-range">
                  <label>{field.label}</label>
                  <div className="range-inputs">
                    <input
                      type="date"
                      value={filters.updatedAtFrom || ''}
                      onChange={(e) => handleFilterChange('updatedAtFrom', e.target.value)}
                    />
                    <span className="range-separator">to</span>
                    <input
                      type="date"
                      value={filters.updatedAtTo || ''}
                      onChange={(e) => handleFilterChange('updatedAtTo', e.target.value)}
                    />
                  </div>
                  <div className="range-quick">
                    <button type="button" className="chip-btn" onClick={() => applyApprovalPreset('week')}>This Week</button>
                    <button type="button" className="chip-btn" onClick={() => applyApprovalPreset('month')}>This Month</button>
                  </div>
                </div>
              );
            }
            return (
              <div key={`filter-${filterId}`} className="filter-item">
                <label>{field.label}</label>
                <input
                  type={getFilterInputType(filterId)}
                  value={filters[filterId] || ''}
                  onChange={(e) => handleFilterChange(filterId, e.target.value)}
                />
              </div>
            );
          })}
          {availableFilters.length === 0 && <div className="muted">No filters configured.</div>}
        </div>

        <div className="table-card">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map(colId => {
                    const field = formFields.find(f => f.id === colId);
                    return <th key={colId}>{field ? field.label : colId}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, idx) => {
                  let parsedData = {};
                  try {
                    parsedData = typeof row.formData === 'string' ? JSON.parse(row.formData) : row.formData;
                  } catch(e) {}

                  return (
                    <tr key={idx}>
                      {columns.map(colId => {
                        const field = formFields.find(f => f.id === colId);
                        const val = getCellValue(row, parsedData, colId);
                        return <td key={`${idx}-${colId}`}>{formatDisplayValue(val, field ? field.type : 'text', colId)}</td>;
                      })}
                    </tr>
                  );
                })}
                {filteredData.length === 0 && (
                  <tr><td colSpan={columns.length} className="empty-table">No data found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-shell">
      {user.role !== 'admin' && <div className="reports-heading">My Reports</div>}
      <div className="reports-grid cards-only report-tabs">
        {reports.map(r => (
          <button
            key={r.id}
            type="button"
            onClick={() => loadReport(r.id)}
            className="report-tab clickable"
          >
            <div className="report-tab-icon">{(r.name || '?').charAt(0).toUpperCase()}</div>
            <div className="report-tab-body">
              <div className="report-title">{r.name}</div>
              <div className="report-meta">{r.description}</div>
            </div>
          </button>
        ))}
        {reports.length === 0 && <div className="empty-reports">No reports assigned to you.</div>}
      </div>
    </div>
  );
}
